import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Form, Input, Select,
	DatePicker, Card, 
	Button,
	Divider
} from "antd";
import { requiredField } from "utils/crudSagaHelper";
import TextArea from "antd/es/input/TextArea";
import { getVendorList } from "../../../../redux/vendor/vendorState";
import {
	getPurchaseOrderDetails,
	doPurchaseOrderCreate,
	doPurchaseOrderUpdate,
} from "../../../../redux/purchaseorder/purchaseOrderState";
import dayjs from "dayjs";
import PurchaseOrderItemForm from "./PurchaseOrderItemForm";
import FormItem from "components/fields/FormItem";
import ButtonWithConfirm from "components/button/buttonwithconfirm";

const PurchaseOrderForm = ({
	recordId,
	mode = "edit",
}) => {
	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const vendorList = useSelector((state) => state.vendor.vendorList?.items);
	const itemTypeList = useSelector((state) => state.purchaseOrder.purchaseOrderDetails.data?.assetTypeList);
	const recordData = useSelector((state) => state.purchaseOrder.purchaseOrderDetails?.data);
	const currentUserId = useSelector((state) => state.auth.user?.id);
	const currencyList = useSelector((state) => state.purchaseOrder.purchaseOrderDetails.data?.currencyList || []);
	const selectedCurrencyId = Form.useWatch("por_currency", form);
	const selectedCurrency =
		currencyList.find(c => c.value === selectedCurrencyId);
	const currencyCode = selectedCurrency?.cur_code ?? "";

	const items = Form.useWatch("items", form) || [];
	const isOwner =
		Number(recordData?.created_by) === Number(currentUserId);

	const isApproved = recordData?.approval_status_code === "POSTAT_APR";

	const deliveryStatus = recordData?.delivery_status_code;
	const paymentStatus = recordData?.payment_status_code;

	const status = {
	canTagForDelivery: isOwner && isApproved && !deliveryStatus,
	canReceive: isOwner && deliveryStatus === "DELSTAT_FDL",

	canTagForInvoice: isOwner && isApproved && !paymentStatus,
	canSendInvoice: isOwner && paymentStatus === "PYMSTAT_FIN",
	canMarkPaid: isOwner && paymentStatus === "PYMSTAT_INV",
	};
	const isNewRecord = !recordId;

	const handleVendorSearch = (value) => {
		if (!value || value.length < 2) return;

    	dispatch(
			getVendorList({
				keyword: value,
				paginate: 20,
			})
    );
	};

	const vendorOptions = React.useMemo(() => {
		const list = Array.isArray(vendorList) ? vendorList : [];
		const base = list
			.filter(v => v?.vdr_id && v?.vdr_name)
			.map(v => ({
				value: v.vdr_id,
				label: v.vdr_name,
			}));

		if (
			recordData?.vendor?.vdr_id &&
			recordData?.vendor?.vdr_name
		) {
			const exists = base.some(
				v => v.value === recordData.vendor.vdr_id
			);

			if (!exists) {
				base.unshift({
					value: recordData.vendor.vdr_id,
					label: recordData.vendor.vdr_name,
				});
			}
		}

		return base;
	}, [vendorList, recordData]);


	
	useEffect(() => {
		if (!recordData) return;

		const items = Array.isArray(recordData.items)
			? recordData.items
			: [];

		form.setFieldsValue({
			...recordData,
			por_purchase_date: recordData.por_purchase_date
				? dayjs(recordData.por_purchase_date)
				: null,
			por_acquired_date: recordData.por_acquired_date
				? dayjs(recordData.por_acquired_date)
				: null,
			por_vendor_id: recordData.por_vendor_id,
			por_currency: recordData.por_currency
				? Number(recordData.por_currency)
				: 2,
			por_remarks: recordData.por_remarks || "",

			items: items.length
				? items.map((item) => ({
						poi_id: item.poi_id,
						poi_item_type_id: item.poi_item_type_id || "",
						item_type_name: item.item_type_name,
						poi_description: item.poi_description || "",
						poi_quantity: item.poi_quantity ?? 1,
						poi_cost: item.poi_cost ?? 0,
				}))
				: [{ poi_quantity: 1, poi_cost: 0 }],
		});
		
	}, [recordId, recordData, form]);
	
	useEffect(() => {
		dispatch(getPurchaseOrderDetails(recordId || 0));
	}, [dispatch, recordId]);

	const formatAmount = (amount) =>
		Number(amount || 0).toLocaleString(undefined, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	
	const computedItems = items.map((item) => {
		const quantity = Number(item.poi_quantity ?? 1);
		const cost = Number(item.poi_cost ?? 0);

		return {
			...item,
			total: quantity * cost,
		};
	});

	const subtotal = computedItems.reduce(
		(sum, item) => sum + item.total,
		0
	);
	const vat = subtotal * 0.12;
	const shippingFee = 0;
	const total = subtotal + shippingFee + vat;

	const onSubmit = (formData) => {
		const payload = {
			por_id: formData.por_id || undefined,
			por_vendor_id: formData.por_vendor_id,
			por_purchase_date: formData.por_purchase_date,
			por_acquired_date: formData.por_acquired_date,
			por_currency: formData.por_currency,
			por_remarks: formData.por_remarks,
			items: formData.items,
			por_total_amount: total,
			action: formData.action,
		};

		if (!payload.por_id) delete payload.por_id;

		formData?.por_id ? dispatch(doPurchaseOrderUpdate(payload)) : dispatch(doPurchaseOrderCreate(payload)) ;

	};

	const getStatusClass = (status) =>
		(status || "")
			.toLowerCase()
			.replace(/\s+/g, "-");
	return (
		
		<Form
			form={form}
			layout="vertical"
			className={mode !== "view" ? "form" : ""}
			onFinish={onSubmit}
		>
			
			<div className="grid grid-cols-3 gap-6 h-screen">

				<div className="col-span-2 h-screen overflow-y-auto pr-2">

					<FormItem name="por_id" hidden>
						<Input type="hidden" />
					</FormItem>

					<FormItem name="action" hidden>
						<Input type="hidden" />
					</FormItem>

					<Card className="mb-4 dark-mode-card">
						<div className="grid grid-cols-3 gap-4">

							<FormItem
								name="por_purchase_date"
								label="Purchased Date"
							>
								{mode !== "view" ? (
									<DatePicker className="input-field w-full" />
								) : (
									<label className="text-base label-value title-text">
										{recordData?.por_purchase_date || "-"}
									</label>
								)}
							</FormItem>

							<FormItem
								name="por_acquired_date"
								label="Acquired Date"
							>
								{mode !== "view" ? (
									<DatePicker className="input-field w-full" />
								) : (
									<label className="text-base label-value dark:text-white">
										{recordData?.por_acquired_date || "-"}
									</label>
								)}
							</FormItem>

							<FormItem
								name="por_currency"
								label="Currency"
							>
								{mode !== "view" ? (
									<Select
										className="input-field"
										options={currencyList.map(c => ({
											...c,
											label: c.cur_code,
										}))}
									/>
								) : (
									<label className="text-base label-value dark:text-white">
										{recordData?.currency_code || "-"}
									</label>
								)}
							</FormItem>

						</div>
					</Card>

					<Form.List name="items">
						{(fields, { add, remove }) => (
							<>
								{fields.map((field) => (
									<div>
										<PurchaseOrderItemForm
											key={field.key}
											itemTypeList={itemTypeList}
											field={field}
											form={form}
											currency={currencyCode}
											remove={remove}
											mode={mode}
										/>
										<Divider/>
									</div>
								))}

								{mode !== "view" && (
									<Button
										type="dashed"
										className="bg-gray-50 dark:bg-gray-100/20 dark:hover:!bg-gray-100/80 muted-text"
										block
										onClick={() =>
											add({
												poi_quantity: 1,
												poi_cost: 0,
											})
										}
									>
										Add Item
									</Button>
								)}
							</>
						)}
					</Form.List>

					<div className="mt-4">
						<FormItem name="por_remarks" label="Remarks">
							{mode !== "view" ? (
								<TextArea
									rows={4}
									className="input-field"
								/>
							) : (
								<label className="text-base label-value dark:text-white">
									{recordData?.por_remarks || "-"}
								</label>
							)}
						</FormItem>
					</div>
				</div>

				<div className="col-span-1 h-screen">
					<div className="sticky top-0">
						<Card className="dark-mode-card">
							<div className="space-y-6">
								<div className="flex items-start justify-between gap-4">
									<div className="flex-1">
										{mode !== "view" ? (
											<FormItem
												name="por_vendor_id"
												label="Vendor"
												rules={[requiredField()]}
											>
												<Select
													placeholder="Search Vendor name"
													className="input-field"
													showSearch
													filterOption={false}
													onSearch={handleVendorSearch}
													options={vendorOptions}
												/>
											</FormItem>
										) : (
											<div className="p-2">
												<h2 className="text-sm font-medium muted-text">Vendor</h2>
												<label className="text-base label-value dark:text-white">
													{recordData?.vendor?.vdr_name || "-"}
												</label>
											</div>
										)}
									</div>
								</div>

								<div>
									<h2 className="font-semibold title-text text-xl mb-4">
										Order Summary
									</h2>

									<div className="space-y-4">
										<div className="flex items-center justify-between muted-text">
											<span>Subtotal ({computedItems.length} Items)</span>
											<span>
												{currencyCode} {" "} 
												{formatAmount(subtotal)}
											</span>
										</div>

										<div className="flex items-center justify-between muted-text">
											<span>Shipping Fee</span>
											<span>
												{currencyCode} {" "} 
												{formatAmount(shippingFee)}
											</span>
										</div>

										<div className="flex items-center justify-between muted-text">
											<span>VAT (12%)</span>
											<span>
												{currencyCode} {" "} 
												{formatAmount(vat)}
											</span>
										</div>

										<div className="border-t pt-4 flex items-center justify-between title-text">
											<span className="text-2xl font-bold">
												Total
											</span>
											<span className="text-2xl font-bold">
												{currencyCode} {" "} 
												{formatAmount(total)}
											</span>
										</div>
									</div>
									{mode === "view" && (
										<div className="border-t mt-4 pt-4">
											<div className="flex items-center justify-between">
												<span className="font-medium muted-text">
													Status
												</span>

												<span className={`text-sm status-indicator ${getStatusClass(recordData?.approval_status_name)}`}>
													{recordData?.approval_status_name || "-"}
												</span>
											</div>
										</div>
									)}
									{recordData?.approval_status_name === "New" &&
										recordData?.created_by !== currentUserId && (
											<ButtonWithConfirm
												block
												className="mt-3 btn-approve w-full"
												confirmLabel="Approve"
												confirmationTitle="Approve Purchase Order"
												confirmationMessage="Are you sure you want to approve this purchase order?"
												confirmHandler={() =>
													dispatch(doPurchaseOrderUpdate({
														por_id: recordData.por_id,
														action: "approve",
													})
												)}
											>
												Approve
											</ButtonWithConfirm>
									)}
									{mode === "view" && isApproved && (
										<>
											<div className="border-t mt-4 pt-4">
												<div className="flex items-center justify-between">
													<span className="font-medium muted-text">
														Delivery Status
													</span>

												<span className={`text-sm bg-white status-indicator ${getStatusClass(recordData?.delivery_status_name)}`}>
														{recordData?.delivery_status_name}
													</span>
												</div>

												{status.canTagForDelivery && (
													<ButtonWithConfirm
														block
														className="mt-3 btn-proceed w-full"
														confirmLabel="Confirm"
														confirmationTitle="Tag as For Delivery"
														confirmationMessage="Are you sure you want to tag this purchase order as For Delivery?"
														confirmHandler={() =>
															dispatch(doPurchaseOrderUpdate({
																por_id: recordData.por_id,
																action: "for_delivery",
															})
														)}
													>
														Tag as For Delivery
													</ButtonWithConfirm>
												)}

												{status.canReceive && (
													<ButtonWithConfirm
														block
														className="mt-3 btn-approve w-full"
														confirmLabel="Confirm"
														confirmationTitle="Tag as Received"
														confirmationMessage="Are you sure you want to mark this purchase order as Received?"
														confirmHandler={() =>
															dispatch(doPurchaseOrderUpdate({
																por_id: recordData.por_id,
																action: "received",
															})
														)}
													>
														Tag as Received
													</ButtonWithConfirm>
												)}
											</div>

											<div className="border-t mt-4 pt-4">
												<div className="flex items-center justify-between">
													<span className="font-medium muted-text">
														Payment Status
													</span>

												<span className={`text-sm bg-white status-indicator ${getStatusClass(recordData?.payment_status_name)}`}>
														{recordData?.payment_status_name}
													</span>
												</div>

												{status.canTagForInvoice && (
													<ButtonWithConfirm
														block
														className="mt-3 btn-proceed w-full"
														confirmLabel="Confirm"
														confirmationTitle="Tag as For Invoice"
														confirmationMessage="Are you sure you want to tag this purchase order as For Invoice?"
														confirmHandler={() =>
															dispatch(doPurchaseOrderUpdate({
																por_id: recordData.por_id,
																action: "for_invoice",
															})
														)}
													>
														Tag as For Invoice
													</ButtonWithConfirm>
												)}

												{status.canSendInvoice && (
													<ButtonWithConfirm
														block
														className="mt-3 btn-proceed w-full"
														confirmLabel="Confirm"
														confirmationTitle="Tag as Invoice Sent"
														confirmationMessage="Are you sure you want to mark this purchase order as Invoice Sent?"
														confirmHandler={() =>
															dispatch(doPurchaseOrderUpdate({																
																por_id: recordData.por_id,
																action: "invoice_sent",}))
														}
													>
														Tag as Invoice Sent
													</ButtonWithConfirm>
												)}

												{status.canMarkPaid && (
													<ButtonWithConfirm
														block
														className="mt-3 btn-approve w-full"
														confirmLabel="Confirm"
														confirmationTitle="Tag as Invoice Paid"
														confirmationMessage="Are you sure you want to mark this purchase order as Invoice Paid?"
														confirmHandler={() =>
															dispatch(doPurchaseOrderUpdate({
																por_id: recordData.por_id,
																action: "invoice_paid",
															})
														)}
													>
														Tag as Invoice Paid
													</ButtonWithConfirm>
												)}
											</div>
										</>
									)}
								</div>
							</div>
							{mode !== "view" && (
								<div className="space-y-2 mt-2">
									<ButtonWithConfirm
										block
										className="btn-base w-full title-text"
										confirmLabel="Save Draft"
										confirmationTitle="Save Draft"
										confirmationMessage="Are you sure you want to save this purchase order as a draft?"
										confirmHandler={() => {
											form.setFieldValue("action", "draft");
											form.submit();
										}}
									>
										{isNewRecord ? "Save Draft" : "Update Draft"}
									</ButtonWithConfirm>
									<ButtonWithConfirm
										block
										className="btn-approve w-full"
										confirmLabel="Submit"
										confirmationTitle="Submit For Approval"
										confirmationMessage="Are you sure you want to submit this purchase order for approval?"
										confirmHandler={() => {
											form.setFieldValue("action", "submit");
											form.submit();
										}}
									>
										Submit For Approval
									</ButtonWithConfirm>
								</div>
							)}
						</Card>
					</div>
				</div>
			</div>
		</Form>
	);
};

export default (PurchaseOrderForm);
